import type { TAppUser, TLoginReq } from '@/types/api/user'
import apiUser from '@/api/modules/user'
import { ELevelType, EUserStatusType, EUserType } from '@/constants/enums'
import router from '@/router'

const initialUserInfo: TAppUser = {
  account: '',
  nickname: '',
  photo: '',
  id: 0,
  agentID: 0,
  balance: 0,
  balance1: 0,
  balance2: 0,
  bindEmail: '',
  bindGoogleCode: 0,
  bindPhone: '',
  chainRechargeAddress: '',
  createdAt: '',
  deep: 0,
  freezeBalance: 0,
  freezeBalance2: 0,
  inBlackList: EUserStatusType.Disable,
  inviteCode: '',
  isAgent: 0,
  isSetPayPassword: 0,
  level: ELevelType.Level1,
  level1InviteIncome: 0,
  level2InviteIncome: 0,
  level3InviteIncome: 0,
  loginIP: '',
  loginRegion: '',
  loginTime: 0,
  registerCode: '',
  registerIP: '',
  registerTime: 0,
  rootAccount: '',
  rootUserID: 0,
  score: 0,
  sign: '',
  startTime: 0,
  totalCost: 0,
  totalIncome: 0,
  totalInvestmentIncome: 0,
  totalInviteIncome: 0,
  totalManualRecharge: 0,
  totalRecharge: 0,
  totalRewardIncome: 0,
  totalWithdraw: 0,
  totalWithdrawFee: 0,
  type: EUserType.Real,
  upAccount: '',
  upInviteCode: '',
  upType: EUserType.Real,
  upUserID: 0,
  updatedAt: '',
  userID: 0,
  validOrderCount: 0,
}

export const useUserStore = defineStore(
  // 唯一ID
  'user',
  () => {
    const settingsStore = useSettingsStore()
    const routeStore = useRouteStore()
    const menuStore = useMenuStore()
    const tabbarStore = useTabbarStore()

    const token = ref(localStorage.token ?? '')
    const userInfo = ref<TAppUser>(localStorage.userInfo ? JSON.parse(localStorage.userInfo) : initialUserInfo)
    const permissions = ref<string[]>([])
    const isLogin = computed(() => {
      if (token.value) {
        return true
      }
      return false
    })

    // 登录（兼容接口返回 data.user 与 mock 的扁平 data）
    async function login(data: TLoginReq) {
      const res = await apiUser.login(data)
      const d = res.data
      const tokenVal = d.token ?? ''
      localStorage.setItem('token', tokenVal)
      localStorage.setItem('userInfo', JSON.stringify(d.user))
      token.value = tokenVal
      userInfo.value = d.user
    }

    // 手动登出
    function logout(redirect = router.currentRoute.value.fullPath) {
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      localStorage.removeItem('token')
      token.value = ''
      router.push({
        name: 'login',
        query: {
          ...(redirect !== settingsStore.settings.home.fullPath && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }).then(logoutCleanStatus)
    }
    // 请求登出
    function requestLogout() {
      // 此处仅清除计算属性 isLogin 中判断登录状态过期的变量，以保证在弹出登录窗口模式下页面展示依旧正常
      localStorage.removeItem('token')
      token.value = ''
      router.push({
        name: 'login',
        query: {
          ...(
            router.currentRoute.value.fullPath !== settingsStore.settings.home.fullPath
            && router.currentRoute.value.name !== 'login'
            && {
              redirect: router.currentRoute.value.fullPath,
            }
          ),
        },
      }).then(logoutCleanStatus)
    }
    // 登出后清除状态
    function logoutCleanStatus() {
      localStorage.removeItem('account')
      localStorage.removeItem('userInfo')
      userInfo.value = initialUserInfo
      permissions.value = []
      settingsStore.updateSettings({}, true)
      tabbarStore.clean()
      routeStore.removeRoutes()
      menuStore.setActived(0)
    }

    // 获取权限
    async function getPermissions() {
      const res = await apiUser.permission()
      permissions.value = res.data.permissions
    }
    // 修改密码
    async function editPassword(data: {
      password: string
      newPassword: string
    }) {
      await apiUser.passwordEdit(data)
    }

    return {
      token,
      permissions,
      isLogin,
      userInfo,
      login,
      logout,
      requestLogout,
      getPermissions,
      editPassword,
    }
  },
)
